import {
  AfterContentInit,
  ChangeDetectorRef,
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output,
  QueryList,
} from '@angular/core';
import { Subscription } from 'rxjs';

/**
 * Collapses the items of a single-line row that don't fit into a trailing
 * "More" dropdown, re-measuring on resize. The consumer hides items at index
 * >= `visibleCount` and renders the "More" trigger only while some overflow:
 *
 *   <nav appOverflowMenu [(visibleCount)]="visibleCount">
 *     <ul>
 *       <li #overflowItem *ngFor="let item of items; let i = index"
 *           [class.d-none]="i >= visibleCount">…</li>
 *     </ul>
 *     <div *ngIf="visibleCount < items.length" ngbDropdown>
 *       <a #overflowMore ngbDropdownToggle>More</a>
 *       <div ngbDropdownMenu>…items.slice(visibleCount)…</div>
 *     </div>
 *   </nav>
 */
@Directive({
  selector: '[appOverflowMenu]',
})
export class OverflowMenuDirective implements AfterContentInit, OnDestroy {

  // Number of items rendered inline; the rest are shown in the "More" dropdown.
  @Input() visibleCount = 0;
  @Output() visibleCountChange = new EventEmitter<number>();

  @ContentChildren('overflowItem', { read: ElementRef, descendants: true })
  items: QueryList<ElementRef<HTMLElement>>;

  @ContentChild('overflowMore', { read: ElementRef })
  moreToggle: ElementRef<HTMLElement>;

  // `any`: this project's TS lib predates the ResizeObserver type.
  private resizeObserver: any = null;
  private itemsSub: Subscription | null = null;

  // Width reserved for the "More" trigger; a generous default until the
  // rendered trigger has been measured.
  private moreToggleFootprintPx = 120;

  // Container-relative right edge of each item, kept while an item is hidden
  // in the dropdown so it can be found to fit again as the container grows.
  private itemRightEdgeCache: number[] = [];

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) { }

  ngAfterContentInit(): void {
    this.attachObserver();
    // Items may render after the directive (async *ngFor data, a loading-guard
    // *ngIf); re-measure whenever the set changes.
    this.itemsSub = this.items.changes.subscribe(() => this.attachObserver());
  }

  private attachObserver(): void {
    if (!this.resizeObserver) {
      const ResizeObserverCtor = (window as any).ResizeObserver;
      if (ResizeObserverCtor) {
        this.resizeObserver = new ResizeObserverCtor(() => {
          this.zone.run(() => this.recompute());
        });
        this.resizeObserver.observe(this.el.nativeElement);
      }
    }
    // One frame, so the items are laid out before their widths are read.
    requestAnimationFrame(() => this.recompute());
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.recompute();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.itemsSub) {
      this.itemsSub.unsubscribe();
      this.itemsSub = null;
    }
  }

  /**
   * Measure the row and update `visibleCount` so the visible items plus a
   * "More" trigger fit. Measures each item's right edge rather than summing
   * offsetWidth, which omits margins and pushes the trigger off the end.
   */
  private recompute(): void {
    if (!this.items) { return; }

    const containerEl = this.el.nativeElement;
    const containerRect = containerEl.getBoundingClientRect();
    const containerWidth = containerRect.width;
    if (containerWidth <= 0) { return; }

    const items = this.items.toArray();
    if (items.length === 0) { return; }

    // Hidden items (display: none) report a 0-width rect; their cached edges
    // are kept.
    for (let i = 0; i < items.length; i++) {
      const r = items[i].nativeElement.getBoundingClientRect();
      if (r.width > 0) {
        this.itemRightEdgeCache[i] = r.right - containerRect.left;
      }
    }

    // Measure the rendered "More" footprint (margins + trailing gap included).
    if (this.moreToggle && this.moreToggle.nativeElement.offsetWidth > 0) {
      const moreContainer = this.moreToggle.nativeElement.parentElement;
      if (moreContainer) {
        const moreRect = moreContainer.getBoundingClientRect();
        const cs = getComputedStyle(moreContainer);
        const marginR = parseFloat(cs.marginRight) || 0;
        this.moreToggleFootprintPx = moreRect.width + marginR + 8;
      }
    }

    let fit = 0;
    for (let i = 0; i < items.length; i++) {
      const rightEdge = this.itemRightEdgeCache[i];
      if (rightEdge === undefined) { break; }
      const isLast = i === items.length - 1;
      const reserve = isLast ? 0 : this.moreToggleFootprintPx;
      if (rightEdge + reserve <= containerWidth) {
        fit = i + 1;
      } else {
        break;
      }
    }

    if (fit !== this.visibleCount) {
      this.visibleCount = fit;
      this.visibleCountChange.emit(fit);
      this.cdr.detectChanges();
      // The change may have inserted or removed the "More" trigger; re-run
      // once its footprint can be measured. Converges: the cache is stable.
      requestAnimationFrame(() => this.recompute());
    }
  }
}
